import { FormProvider } from 'react-hook-form'
import { useState } from 'react'
import { CalendarDays, LayoutGrid, Megaphone } from 'lucide-react'

import { useCampaignsTable } from '@/hooks/features/campaigns'

import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { NoData } from '@/components/ui/data-table/NoData'
import { Pagination } from '@/components/ui/pagination-controls'
import { cn } from '@/lib/utils'
import { ConfirmDialog, PageHeader, StatsCards } from '@/components/atoms'

import { CampaignsFilters } from './CampaignsFilters'
import { CampaignCardGrid } from './CampaignCardGrid'
import { CampaignCalendar } from './CampaignCalendar'

const VIEWS = [
  { id: 'cards', label: 'Cards', icon: LayoutGrid },
  { id: 'calendar', label: 'Calendar', icon: CalendarDays },
] as const

export const CampaignsTable = () => {
  const ctx = useCampaignsTable()
  const isEmpty = !ctx.loading && ctx.data.length === 0
  const [view, setView] = useState<'cards' | 'calendar'>('cards')

  return (
    <div className="space-y-4">
      <PageHeader
        title="Campaigns"
        description="Time-limited promotions on your products."
        onAddClick={ctx.openCreateBuilder}
        addButtonText="New campaign"
      />

      <StatsCards
        stats={[
          { title: 'Total', value: ctx.stats.total_count, icon: Megaphone },
          { title: 'Active',   value: ctx.stats.active_count },
          { title: 'Inactive', value: ctx.stats.inactive_count },
          { title: 'Archived', value: ctx.stats.archived_count },
        ]}
      />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              <Pagination.PageSizeDropdown
                pageSize={ctx.pagination.pageSize}
                onPageSizeChange={ctx.setPageSize}
              />
              <FormProvider {...ctx.formMethods}>
                <CampaignsFilters />
              </FormProvider>
            </div>
            <div className="order-first flex gap-1 lg:order-last" role="group" aria-label="View">
              {VIEWS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={view === id}
                  onClick={() => setView(id)}
                  className={cn(
                    'flex h-7 shrink-0 items-center gap-1.5 rounded-md px-3 text-sm font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring',
                    view === id ? 'bg-selected text-ink' : 'text-text-2 hover:bg-subdued hover:text-ink',
                  )}
                >
                  <Icon className="size-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isEmpty ? (
            <NoData
              title="No campaigns yet"
              description="Create your first promotion to get started."
              btnText="New campaign"
              openCreateDialog={ctx.openCreateBuilder}
            />
          ) : (
            <>
              {view === 'calendar' ? (
                <CampaignCalendar onEdit={ctx.openEditBuilder} />
              ) : (
                <CampaignCardGrid
                  data={ctx.data}
                  loading={ctx.loading}
                  onPreview={ctx.previewOffer}
                  previewLoading={ctx.previewLoading}
                  onEdit={ctx.openEditBuilder}
                  onToggle={ctx.toggleStatus}
                  onDuplicate={ctx.duplicate}
                  onDelete={ctx.handleDeleteClick}
                  onRestore={ctx.handleRestore}
                />
              )}
              {view === 'cards' && (
                <Pagination.Buttons
                  currentPage={ctx.pagination.currentPage}
                  totalItems={ctx.pagination.totalCount}
                  pageSize={ctx.pagination.pageSize}
                  onPageChange={ctx.setPage}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={ctx.deleteDialogOpen}
        onOpenChange={ctx.setDeleteDialogOpen}
        title="Delete campaign?"
        description={`This deactivates "${ctx.campaignToDelete?.name}" immediately. You can restore it later.`}
        confirmText={ctx.deleteLoading ? 'Deleting...' : 'Delete'}
        onConfirm={ctx.handleDeleteConfirm}
        loading={ctx.deleteLoading}
        variant="destructive"
      />
    </div>
  )
}
